import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useSearchParams, Link, useNavigate } from 'react-router-dom';
import { Box, Container, Typography, Button, Skeleton, Paper, Chip } from '@mui/material';
import { ArrowBack, Sensors } from '@mui/icons-material';
import NavBar from '../../../Components/NavBar/navbar';
import ReplayPlayer from '../../../Components/Arcade/ReplayPlayer';
import ArcadeHubNav, { arcadeHubPath, ARCADE_SECTIONS } from '../ArcadeHubNav';
import { API_ENDPOINTS } from '../../../utils/config';
import { THEME } from '../../../theme';
import usePageTitle from '../../../Components/Arcade/ui/usePageTitle';
import { EMPTY_LIVE_HISTORY, liveStateUrl, mergeLiveHistory } from './liveHistory';

const POLL_MS = 2000;

/**
 * Read-only spectator view for an in-progress SDK session. Polls the
 * orchestrator's live-state proxy (GM `/info` snapshot) and renders it
 * through the same ReplayPlayer/adapters used for completed-run replays,
 * with playback tailing new frames as they arrive (`live` mode).
 */
const SpectateSessionPage = () => {
    usePageTitle('Watch live');
    const { sessionId } = useParams();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const gameIdHint = searchParams.get('game');
    const [run, setRun] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [ended, setEnded] = useState(false);
    const mountedRef = useRef(true);
    // History held across polls; each poll asks only for entries since `next`.
    const historyRef = useRef(EMPTY_LIVE_HISTORY);
    const dashboardPath = arcadeHubPath(ARCADE_SECTIONS.dashboard);
    const closeReplay = useCallback(() => {
        navigate(dashboardPath);
    }, [navigate, dashboardPath]);

    const poll = useCallback(async () => {
        try {
            const res = await fetch(liveStateUrl(API_ENDPOINTS.SDK_SESSION_STATE(sessionId), historyRef.current));
            if (res.status === 404) {
                if (mountedRef.current) setEnded(true);
                return;
            }
            if (!res.ok) throw new Error(`status ${res.status}`);
            const data = await res.json();
            if (!mountedRef.current) return;
            historyRef.current = mergeLiveHistory(historyRef.current, data);
            if (Array.isArray(data.history)) data.history = historyRef.current.entries;
            const hardOver = Boolean(
                data.is_game_over || data.hard_game_over || data.session_over
                || data.status === 'completed' || data.status === 'stopped'
            );
            if (hardOver) {
                setEnded(true);
            }
            setRun((prev) => ({
                ...(prev || {}),
                ...data,
                id: sessionId,
                run_id: sessionId,
                session_id: sessionId,
                game_id: data.game_id || gameIdHint || (prev && prev.game_id),
            }));
            setError(null);
        } catch (e) {
            if (mountedRef.current) setError('Failed to load live session state');
        } finally {
            if (mountedRef.current) setLoading(false);
        }
    }, [sessionId, gameIdHint]);

    useEffect(() => {
        mountedRef.current = true;
        let timer;
        async function tick() {
            if (!mountedRef.current) return;
            await poll();
            if (mountedRef.current && !ended) timer = setTimeout(tick, POLL_MS);
        }
        setLoading(true);
        tick();
        return () => {
            mountedRef.current = false;
            if (timer) clearTimeout(timer);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [poll, ended]);

    return (
        <Box sx={{ bgcolor: THEME.bg, minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
            <NavBar />
            <Box sx={{ flexGrow: 1, pt: 12, pb: 8 }}>
                <Container maxWidth="md">
                    <ArcadeHubNav activeSection={ARCADE_SECTIONS.dashboard} />

                    <Button
                        component={Link}
                        to={dashboardPath}
                        startIcon={<ArrowBack />}
                        sx={{ color: THEME.secondary, mb: 3 }}
                    >
                        Back to Dashboard
                    </Button>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3, flexWrap: 'wrap' }}>
                        <Typography variant="h4" sx={{ color: 'white', fontWeight: 800 }}>
                            Spectating Live Session
                        </Typography>
                        {!ended && (
                            <Chip
                                size="small"
                                icon={<Sensors sx={{ fontSize: '14px !important' }} />}
                                label="Live"
                                sx={{
                                    bgcolor: 'rgba(44, 187, 93, 0.15)',
                                    color: THEME.primary,
                                    fontWeight: 700,
                                    '& .MuiChip-icon': { color: THEME.primary },
                                }}
                            />
                        )}
                    </Box>

                    {loading && !run && (
                        <Paper sx={{ p: 3, bgcolor: THEME.cardBg, border: `1px solid ${THEME.border}`, borderRadius: 3 }}>
                            <Skeleton variant="text" width="40%" sx={{ bgcolor: 'rgba(255,255,255,0.08)' }} />
                            <Skeleton variant="rectangular" height={340} sx={{ mt: 2, bgcolor: 'rgba(255,255,255,0.08)', borderRadius: 2 }} />
                        </Paper>
                    )}

                    {ended && (
                        <Paper sx={{ p: 4, bgcolor: THEME.cardBg, border: `1px solid ${THEME.border}`, borderRadius: 3, textAlign: 'center', mb: 3 }}>
                            <Typography sx={{ color: THEME.secondary }}>
                                This session has ended. It may show up under Recent Games shortly, with a full replay.
                            </Typography>
                        </Paper>
                    )}

                    {!loading && !run && error && (
                        <Paper sx={{ p: 4, bgcolor: THEME.cardBg, border: `1px solid ${THEME.border}`, borderRadius: 3, textAlign: 'center' }}>
                            <Typography sx={{ color: THEME.accent, fontWeight: 'bold', mb: 1 }}>
                                Unable to load this live session.
                            </Typography>
                            <Typography variant="body2" sx={{ color: THEME.secondary }}>
                                The game may have just ended. Check the dashboard for its result.
                            </Typography>
                        </Paper>
                    )}

                    {!loading && !run && !error && ended && (
                        <Paper sx={{ p: 4, bgcolor: THEME.cardBg, border: `1px solid ${THEME.border}`, borderRadius: 3, textAlign: 'center' }}>
                            <Typography sx={{ color: THEME.secondary }}>
                                This session finished before its state could be captured — nothing to spectate here.
                            </Typography>
                        </Paper>
                    )}

                    {run && (
                        <>
                            {(run.mode === 'training' || run.training_stats) && (
                                <Paper sx={{
                                    p: 2, mb: 2, bgcolor: THEME.cardBg,
                                    border: `1px solid ${THEME.border}`, borderRadius: 3,
                                }}>
                                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                                        <Chip
                                            size="small"
                                            label="Training"
                                            sx={{ bgcolor: 'rgba(255, 193, 7, 0.12)', color: '#ffc107', fontWeight: 700 }}
                                        />
                                        <Typography variant="body2" sx={{ color: 'white' }}>
                                            Round {(run.training_stats?.round_index ?? run.round_index) || '—'}
                                            {' / '}
                                            {(run.training_stats?.num_episodes ?? run.num_episodes) || '—'}
                                        </Typography>
                                        {run.training_stats?.wins && (
                                            <Typography variant="caption" sx={{ color: THEME.secondary }}>
                                                Wins{' '}
                                                {Object.entries(run.training_stats.wins)
                                                    .map(([k, v]) => `${k.replace('player_', 'P')}:${v}`)
                                                    .join(' · ')}
                                                {run.training_stats.draws != null
                                                    ? ` · Draws:${run.training_stats.draws}`
                                                    : ''}
                                            </Typography>
                                        )}
                                        {run.soft_game_over && !ended && (
                                            <Chip size="small" label="Round over (soft)" sx={{ color: THEME.secondary }} />
                                        )}
                                        {ended && (
                                            <Chip size="small" label="Session over" sx={{ color: THEME.accent }} />
                                        )}
                                    </Box>
                                    {Array.isArray(run.training_stats?.round_results)
                                        && run.training_stats.round_results.length > 0 && (
                                        <Typography variant="caption" sx={{ color: '#888', display: 'block', mt: 1 }}>
                                            Results:{' '}
                                            {run.training_stats.round_results.map((rr) => {
                                                const label = rr.result === 'draw' || !rr.winner_id
                                                    ? (rr.result === 'draw' || !rr.result ? 'Draw' : rr.result)
                                                    : (rr.winner_id === 'player_1' ? 'P1' : rr.winner_id === 'player_2' ? 'P2' : rr.winner_id);
                                                const pretty = label === 'player_1' ? 'P1'
                                                    : label === 'player_2' ? 'P2'
                                                        : label === 'draw' ? 'Draw'
                                                            : label;
                                                return `R${rr.round_index}:${pretty}`;
                                            }).join(' · ')}
                                        </Typography>
                                    )}
                                </Paper>
                            )}
                            <ReplayPlayer run={run} live={!ended} autoPlay initialLoop={false} onClose={closeReplay} />
                            <Typography variant="caption" sx={{ color: '#666', display: 'block', mt: 2 }}>
                                Live view catches up to the latest state as new moves happen.
                                {/* polls the session every {Math.round(POLL_MS / 1000)}s — not a
                                frame-perfect stream. Playback catches up to the latest state as new moves happen. */}
                            </Typography>
                        </>
                    )}
                </Container>
            </Box>
        </Box>
    );
};

export default SpectateSessionPage;
